import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Switch,
  Chip,
  IconButton,
  Paper,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CookieIcon from "@mui/icons-material/Cookie";
import TuneIcon from "@mui/icons-material/Tune";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SecurityIcon from "@mui/icons-material/Security";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { COOKIE_POLICY_MARKDOWN } from "../../legal/cookiePolicyText";

const COOKIE_CONSENT_KEY = "pm_cookie_consent";

const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return (
    <Slide
      direction="up"
      ref={ref}
      {...props}
      timeout={{ enter: 1100, exit: 500 }}
      easing={{
        enter: "cubic-bezier(0.08, 0.82, 0.17, 1)",
        exit: "cubic-bezier(0.4, 0, 1, 1)",
      }}
    />
  );
});

// Crisp light-mode markdown styling
const lightMarkdownSx = {
  fontSize: "0.86rem",
  color: "#334155",
  lineHeight: 1.65,
  wordBreak: "break-word",
  "& > :first-of-type": { mt: 0 },
  "& > :last-child": { mb: 0 },
  "& p": { m: 0, mb: 1.2 },
  "& p:last-child": { mb: 0 },
  "& strong": { fontWeight: 700, color: "#0f172a" },
  "& em": { fontStyle: "italic", color: "#475569" },
  "& a": {
    color: "#059669",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  "& ul, & ol": {
    m: "0 0 0.85rem",
    pl: "1.25rem",
  },
  "& li": { mb: 0.4 },
  "& li::marker": { color: "#10b981" },
  "& h1, & h2, & h3, & h4": {
    m: "1rem 0 0.5rem",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    color: "#0f172a",
    lineHeight: 1.3,
  },
  "& h1": { fontSize: "1.1rem" },
  "& h2": { fontSize: "1.02rem" },
  "& h3, & h4": { fontSize: "0.92rem" },
  "& hr": {
    border: 0,
    borderTop: "1px solid #e2e8f0",
    my: 1.5,
  },
  "& blockquote": {
    m: "0 0 0.85rem",
    pl: 1.5,
    borderLeft: "4px solid #10b981",
    color: "#475569",
    bgcolor: "#f8fafc",
    py: 0.5,
    borderRadius: "0 8px 8px 0",
  },
  "& code": {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    fontSize: "0.8rem",
    px: 0.6,
    py: 0.2,
    borderRadius: "6px",
    bgcolor: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#059669",
  },
  "& table": {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    my: 2,
    fontSize: "0.78rem",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(15, 23, 42, 0.04)",
    tableLayout: "fixed",
  },
  "& thead": {
    bgcolor: "#f8fafc",
  },
  "& thead th": {
    textAlign: "left",
    fontWeight: 700,
    color: "#065f46",
    bgcolor: "#ecfdf5",
    borderBottom: "2px solid #a7f3d0",
    py: 1.2,
    px: 1.2,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    whiteSpace: "normal",
    wordBreak: "break-word",
    verticalAlign: "bottom",
    borderRight: "1px solid #cbd5e1",
  },
  "& th:nth-of-type(1)": { width: "16%" },
  "& th:nth-of-type(2)": { width: "12%" },
  "& th:nth-of-type(3)": { width: "9%" },
  "& th:nth-of-type(4)": { width: "13%" },
  "& th:nth-of-type(5)": { width: "25%" },
  "& th:nth-of-type(6)": { width: "11%" },
  "& th:nth-of-type(7)": { width: "14%" },
  "& tbody tr": {
    transition: "background-color 0.15s ease",
  },
  "& tbody tr:hover": {
    bgcolor: "#f0fdf4",
  },
  "& tbody tr:nth-of-type(even)": {
    bgcolor: "#f8fafc",
  },
  "& td": {
    px: 1.2,
    py: 1.1,
    borderBottom: "1px solid #e2e8f0",
    borderRight: "1px solid #e2e8f0",
    verticalAlign: "top",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.5,
    color: "#334155",
  },
  "& td:last-child, & th:last-child": {
    borderRight: "none",
  },
  "& tbody tr:last-child td": {
    borderBottom: "none",
  },
  "& tbody tr:nth-of-type(even) td": {
    bgcolor: "#f8fafc",
  },
};

export default function CookieConsentCMP() {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [functionalConsent, setFunctionalConsent] = useState(false);

  // Read saved consent on mount & handle splash screen delay
  useEffect(() => {
    try {
      const savedRaw = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (savedRaw) {
        const parsed = JSON.parse(savedRaw);
        setFunctionalConsent(Boolean(parsed.functional));
        setBannerVisible(false);
        return;
      }
    } catch (e) {
      // ignore
    }

    setFunctionalConsent(false);

    // Check if splash screen is currently active (e.g. initial visit to marketing home page)
    const isSplashDone = sessionStorage.getItem("patientMeshSplashDone") === "true";
    const isMarketingPath = window.location.pathname === "/" || window.location.pathname === "";

    if (!isSplashDone && isMarketingPath) {
      // Keep banner hidden while splash is playing
      setBannerVisible(false);

      let timerId = null;
      let fallbackId = null;

      const handleSplashDone = () => {
        // Smoothly delay showing cookie banner until splash screen fade animation completes
        timerId = setTimeout(() => {
          setBannerVisible(true);
        }, 400);
      };

      window.addEventListener("patient-mesh-splash-complete", handleSplashDone, { once: true });

      // Safety fallback in case splash event is missed
      fallbackId = setTimeout(() => {
        setBannerVisible(true);
      }, 2200);

      return () => {
        window.removeEventListener("patient-mesh-splash-complete", handleSplashDone);
        if (timerId) clearTimeout(timerId);
        if (fallbackId) clearTimeout(fallbackId);
      };
    } else {
      // Splash screen is already done or user is on another route -> show immediately
      setBannerVisible(true);
    }
  }, []);

  // Custom Event / Global Window method to re-open modal anytime
  const handleOpenModal = useCallback(() => {
    try {
      const savedRaw = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (savedRaw) {
        const parsed = JSON.parse(savedRaw);
        setFunctionalConsent(Boolean(parsed.functional));
      }
    } catch (e) {
      // ignore
    }
    setActiveTab(0);
    setModalOpen(true);
    // Note: bannerVisible is NOT closed when opening modal, so modal stays on top of banner!
  }, []);

  useEffect(() => {
    window.openCookieConsentModal = handleOpenModal;

    const handleCustomEvent = () => handleOpenModal();
    window.addEventListener("open-cookie-consent", handleCustomEvent);

    return () => {
      delete window.openCookieConsentModal;
      window.removeEventListener("open-cookie-consent", handleCustomEvent);
    };
  }, [handleOpenModal]);

  const saveConsent = (functionalState) => {
    const payload = {
      necessary: true,
      functional: Boolean(functionalState),
      timestamp: new Date().toISOString(),
      version: "1.0",
    };

    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(payload));
      if (!functionalState) {
        localStorage.removeItem("i18nextLng");
      }
    } catch (e) {
      console.error("[CMP] Failed to save cookie consent to localStorage:", e);
    }

    setFunctionalConsent(Boolean(functionalState));
    // Both banner and modal close when consent is explicitly saved/accepted/rejected!
    setBannerVisible(false);
    setModalOpen(false);

    window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: payload }));
  };

  const handleAcceptAll = () => {
    saveConsent(true);
  };

  const handleRejectAll = () => {
    saveConsent(false);
  };

  const handleSavePreferences = () => {
    saveConsent(functionalConsent);
  };

  // Only closes the modal, leaving the bottom banner visible beneath it!
  const handleCloseModalOnly = () => {
    setModalOpen(false);
  };

  // Disables closing modal on backdrop click or ESC key press
  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    setModalOpen(false);
  };

  return (
    <>
      {/* ── 1. KATMAN: ALTTAN SLIDE EDEN BANNER (z-index: 1400) ──────────────── */}
      {bannerVisible && (
        <Slide
          in={bannerVisible}
          direction="up"
          mountOnEnter
          unmountOnExit
          timeout={{ enter: 1300, exit: 500 }}
          easing={{
            enter: "cubic-bezier(0.08, 0.82, 0.17, 1)",
            exit: "cubic-bezier(0.4, 0, 1, 1)",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              position: "fixed",
              bottom: { xs: 12, md: 20 },
              left: { xs: 12, md: 24 },
              right: { xs: 12, md: 24 },
              maxWidth: 960,
              margin: "0 auto",
              zIndex: 1400,
              p: { xs: 2.5, md: 3 },
              borderRadius: "18px",
              backdropFilter: "blur(16px)",
              bgcolor: "#ffffff",
              border: "1px solid rgba(16, 185, 129, 0.35)",
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.12), 0 0 20px rgba(16, 185, 129, 0.1)",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Üst Kısım: İkon + Başlık + Açıklama Metni */}
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: "12px",
                    bgcolor: "#ecfdf5",
                    color: "#059669",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 0.2,
                    border: "1px solid #a7f3d0",
                    flexShrink: 0,
                  }}
                >
                  <CookieIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.02rem",
                      mb: 0.6,
                      color: "#0f172a",
                    }}
                  >
                    PatientMesh’te Çerez ve Gizlilik Tercihleri
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      lineHeight: 1.65,
                      color: "#334155",
                    }}
                  >
                    PatientMesh’te; sitenin güvenli, kesintisiz ve doğru çalışabilmesi
                    amacıyla zorunlu çerezler kullanıyoruz. Fonksiyonel çerezleri ise yalnızca
                    tercih etmeniz halinde arayüz tercihlerinizi hatırlamak için etkinleştiririz.
                    Çerez tercihlerinizi dilediğiniz zaman değiştirebilir; ayrıntılı bilgiye{" "}
                    <strong style={{ color: "#0f172a" }}>“Tüm Tercihleri Yönet”</strong> seçeneğinden veya Çerez Politikamızdan
                    ulaşabilirsiniz.
                  </Typography>
                </Box>
              </Box>

              {/* Alt Kısım: METNİN ALTINDA MERKEZE HİZALANMIŞ (ORTALANMIŞ) BUTONLAR */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  justifyContent: "center", // BUTONLAR ORTALANDI!
                  gap: 1.5,
                  pt: 1.5,
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={handleRejectAll}
                  sx={{
                    borderColor: "#cbd5e1",
                    color: "#475569",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "10px",
                    px: 3,
                    py: 0.9,
                    width: { xs: "100%", sm: "auto" },
                    minWidth: 140,
                    bgcolor: "#ffffff",
                    "&:hover": {
                      borderColor: "#94a3b8",
                      bgcolor: "#f8fafc",
                      color: "#0f172a",
                    },
                  }}
                >
                  Tümünü Reddet
                </Button>

                <Button
                  variant="outlined"
                  size="medium"
                  onClick={handleOpenModal}
                  startIcon={<TuneIcon />}
                  sx={{
                    borderColor: "#10b981",
                    color: "#059669",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "10px",
                    px: 3,
                    py: 0.9,
                    width: { xs: "100%", sm: "auto" },
                    minWidth: 160,
                    bgcolor: "#ffffff",
                    "&:hover": {
                      borderColor: "#059669",
                      bgcolor: "#ecfdf5",
                    },
                  }}
                >
                  Tüm Tercihleri Yönet
                </Button>

                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleAcceptAll}
                  sx={{
                    bgcolor: "#10b981",
                    color: "#ffffff",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: "10px",
                    px: 3.5,
                    py: 0.9,
                    width: { xs: "100%", sm: "auto" },
                    minWidth: 160,
                    boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
                    "&:hover": {
                      bgcolor: "#059669",
                      boxShadow: "0 6px 18px rgba(5, 150, 105, 0.4)",
                    },
                  }}
                >
                  Tümünü Kabul Et
                </Button>
              </Box>
            </Box>
          </Paper>
        </Slide>
      )}

      {/* ── 2. KATMAN: POP-UP BANNER'IN ÜSTÜNDE AÇILAN MODAL (z-index: 1600) ────── */}
      <Dialog
        open={modalOpen}
        onClose={handleDialogClose}
        onWheel={(e) => e.stopPropagation()}
        TransitionComponent={SlideTransition}
        transitionDuration={{ enter: 1100, exit: 500 }}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{
          zIndex: 1600, // Modal, alttaki banner'ın (1400) üstünde açılır!
        }}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            bgcolor: "#ffffff",
            backgroundImage: "none",
            boxShadow: "0 25px 65px rgba(15, 23, 42, 0.25)",
            border: "1px solid #e2e8f0",
          },
        }}
      >
        {/* Modal Başlık - X İkonu Sadece Üstteki Modalı Kapatır */}
        <DialogTitle
          sx={{
            m: 0,
            p: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #f1f5f9",
            bgcolor: "#ffffff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: "10px",
                bgcolor: "#ecfdf5",
                color: "#059669",
                display: "flex",
                border: "1px solid #a7f3d0",
              }}
            >
              <TuneIcon />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#0f172a" }}>
                Çerez ve Gizlilik Tercih Yönetimi
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Medaxxa Medikal Sanayi Ticaret A.Ş. / PatientMesh
              </Typography>
            </Box>
          </Box>

          {/* X Butonu: Sadece Modalı kapatır, alttaki banner görünür kalır */}
          <IconButton onClick={handleCloseModalOnly} sx={{ color: "#64748b", "&:hover": { bgcolor: "#f1f5f9" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Modal Sekmeleri (Tabs) — Yarı Yarıya (Full Width 50% - 50%) */}
        <Box sx={{ borderBottom: "1px solid #e2e8f0", px: 0, pt: 1, bgcolor: "#ffffff" }}>
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            variant="fullWidth"
            textColor="inherit"
            indicatorColor="primary"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#10b981",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                minHeight: 48,
                maxWidth: "none",
                color: "#64748b",
                "&.Mui-selected": {
                  color: "#059669",
                  fontWeight: 700,
                },
              },
            }}
          >
            <Tab icon={<TuneIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Tüm Tercihleri Yönet" />
            <Tab
              icon={<DescriptionIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Çerez Aydınlatma Metni"
            />
          </Tabs>
        </Box>

        {/* Modal Gövde */}
        <DialogContent dividers sx={{ p: 3, borderColor: "#f1f5f9", bgcolor: "#ffffff" }}>
          {/* ── TAB 0: ÇEREZ TERCİHLERİ ────────────────────────────────────── */}
          {activeTab === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Kategori 1: Zorunlu Çerezler */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: "14px",
                  bgcolor: "#f8fafc",
                  borderColor: "#e2e8f0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <SecurityIcon sx={{ color: "#059669" }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a" }}>
                      Zorunlu Çerezler
                    </Typography>
                    <Chip
                      label="Kilitli / Zorunlu"
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        bgcolor: "#dcfce7",
                        color: "#15803d",
                        border: "1px solid #86efac",
                      }}
                    />
                  </Box>
                  <Switch checked={true} disabled color="success" />
                </Box>
                <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
                  Güvenli oturum açma, bot saldırılarına karşı koruma (reCAPTCHA) ve sistem
                  güvenliği için zorunludur. Bu çerezler kapatılamaz; aksi takdirde platformun temel
                  fonksiyonları çalışamaz.
                </Typography>
              </Paper>

              {/* Kategori 2: Fonksiyonel Çerezler */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: "14px",
                  bgcolor: functionalConsent ? "#f0fdf4" : "#f8fafc",
                  borderColor: functionalConsent ? "#86efac" : "#e2e8f0",
                  transition: "all 0.2s ease",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CheckCircleOutlineIcon
                      sx={{
                        color: functionalConsent ? "#059669" : "#94a3b8",
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a" }}>
                      Fonksiyonel Çerezler
                    </Typography>
                    <Chip
                      label={functionalConsent ? "Etkin" : "İsteğe Bağlı"}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        bgcolor: functionalConsent ? "#dcfce7" : "#e2e8f0",
                        color: functionalConsent ? "#15803d" : "#475569",
                        border: functionalConsent ? "1px solid #86efac" : "1px solid #cbd5e1",
                      }}
                    />
                  </Box>
                  <Switch
                    checked={functionalConsent}
                    onChange={(e) => setFunctionalConsent(e.target.checked)}
                    color="success"
                  />
                </Box>
                <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
                  Arayüz dili (Türkçe/İngilizce), tema tercihleri (Karanlık/Aydınlık) ve etkileşimli
                  Google Harita/Randevu entegrasyonlarının çalışması için kullanılır. Reddetmeniz
                  halinde temel PatientMesh hizmetleri çalışmaya devam eder, yalnızca arayüz kişiselleştirmeleri etkilenir.
                </Typography>
              </Paper>
            </Box>
          )}

          {/* ── TAB 1: ÇEREZ AYDINLATMA METNİ (MARKDOWN) ──────────────────── */}
          {activeTab === 1 && (
            <Box
              sx={{
                maxHeight: "55vh",
                overflowY: "auto",
                pr: 1,
                ...lightMarkdownSx,
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "#10b981",
                  borderRadius: 3,
                },
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {COOKIE_POLICY_MARKDOWN}
              </ReactMarkdown>
            </Box>
          )}
        </DialogContent>

        {/* Modal Alt Butonlar: Reddet veya Kaydet Seçildiğinde İkisi de Kapanır! */}
        <DialogActions
          sx={{
            p: 2.5,
            borderColor: "#f1f5f9",
            justifyContent: "space-between",
            bgcolor: "#f8fafc",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleRejectAll}
            sx={{
              borderColor: "#cbd5e1",
              color: "#475569",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              px: 2.5,
              bgcolor: "#ffffff",
              "&:hover": {
                borderColor: "#94a3b8",
                bgcolor: "#f1f5f9",
                color: "#0f172a",
              },
            }}
          >
            Tümünü Reddet
          </Button>

          <Button
            variant="contained"
            onClick={handleSavePreferences}
            sx={{
              bgcolor: "#10b981",
              color: "#ffffff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "10px",
              px: 3.5,
              boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
              "&:hover": {
                bgcolor: "#059669",
                boxShadow: "0 6px 18px rgba(5, 150, 105, 0.4)",
              },
            }}
          >
            Tercihleri Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
